import { css } from "glamor";
import React, { useState } from "react";
import { colors, spacing } from "../library/theme";
import { uiColors } from "../library/theme";
import { formatRemainingTime } from "../../utils/studyPlanFormatters";
import StudyPlanLessonRow from "./StudyPlanLessonRow";
import StudyPlanMockExamRow from "./StudyPlanMockExamRow";

// ── card container ──────────────────────────────────────────────────────────

const cardClass = css({
  border: `1px solid ${colors.greyLight}`,
  borderRadius: 12,
  boxShadow: `0px 2px 4px ${uiColors.border}`,
  overflow: "hidden",
  "& + &": { marginTop: spacing.m },
});

// ── header ──────────────────────────────────────────────────────────────────

const headerClass = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: spacing.m,
  padding: `${spacing.m} ${spacing.l}`,
  flexWrap: "wrap",
  "@media (max-width: 768px)": {
    flexDirection: "column",
    alignItems: "flex-start",
    padding: `${spacing.m} ${spacing.m}`,
    gap: spacing.s,
  },
});

const headerLeftClass = css({
  display: "flex",
  alignItems: "center",
  gap: spacing.s,
  flex: 1,
  minWidth: 0,
});


const headerTitleAreaClass = css({
  display: "flex",
  flexDirection: "column",
  gap: 4,
  flex: 1,
  minWidth: 0,
});

const titleStyle = {
  fontSize: 16,
  fontWeight: 700,
  color: colors.greyShadow,
  margin: 0,
  lineHeight: 1.3,
};

const progressBarWrapperClass = css({
  display: "flex",
  alignItems: "center",
  gap: spacing.xs,
});

const progressBarTrackClass = css({
  flex: 1,
  height: 6,
  backgroundColor: colors.greyLighter,
  borderRadius: 3,
  overflow: "hidden",
});

const progressBarFillClass = css({
  height: "100%",
  borderRadius: 3,
  backgroundColor: colors.primary,
  transition: "width 300ms ease",
});

const progressBarFillCompletedClass = css({
  height: "100%",
  borderRadius: 3,
  backgroundColor: "#43A047",
  transition: "width 300ms ease",
});

const progressLabelStyle = {
  fontSize: 11,
  fontWeight: 600,
  color: colors.greyDark,
  whiteSpace: "nowrap",
};

const headerRightClass = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: 4,
  flexShrink: 0,
  "@media (max-width: 768px)": {
    alignItems: "flex-start",
  },
});

const statusPillClass = css({
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  borderRadius: 4,
  padding: "3px 8px",
  whiteSpace: "nowrap",
});

const statusTodayInProgressClass = css({
  backgroundColor: "#E8F5E9",
  color: "#43A047",
});

const statusTodayCompletedClass = css({
  backgroundColor: "#E8F5E9",
  color: "#43A047",
});



const remainingTimeStyle = {
  fontSize: 12,
  color: colors.greyDark,
  lineHeight: 1.3,
};

const remainingTimeBoldStyle = {
  fontWeight: 700,
  color: colors.greyShadow,
};

const remainingTimeBoldAlertStyle = {
  fontWeight: 700,
  color: "#E65100",
};

// ── collapse toggle ─────────────────────────────────────────────────────────

const collapseToggleClass = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  padding: `${spacing.xs} 0`,
  color: colors.greyDark,
  fontSize: 12,
  fontWeight: 600,
  gap: 4,
  transition: "color 150ms ease",
  ":hover": { color: colors.primary },
});

const chevronStyle = (isOpen) => ({
  transition: "transform 200ms ease",
  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
  fontSize: 14,
});

// ── module header ───────────────────────────────────────────────────────────

const moduleHeaderClass = css({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  color: colors.greyDark,
  padding: `${spacing.s} ${spacing.m} 2px`,
});

// ── lesson list ─────────────────────────────────────────────────────────────

const lessonListClass = css({
  padding: `0 ${spacing.m} ${spacing.m}`,
  display: "flex",
  flexDirection: "column",
  gap: 4,
});


// ── helpers ─────────────────────────────────────────────────────────────────

const WEEKDAY_NAMES = [
  "Domingo",
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado",
];

function getWeekdayName(isoDate) {
  const [year, month, dayNum] = isoDate.split("-").map(Number);
  const date = new Date(year, month - 1, dayNum);
  return WEEKDAY_NAMES[date.getDay()];
}

function getStatusPill(day) {
  const allCompleted = day.dailyProgressPercent >= 100;

  if (day.isToday) {
    if (allCompleted) {
      return {
        label: "Concluído!",
        className: css(statusPillClass, statusTodayCompletedClass),
      };
    }
    return {
      label: "Missão do dia",
      className: css(statusPillClass, statusTodayInProgressClass),
    };
  }

  return null;
}

function getRemainingSeconds(items) {
  return items.reduce((sum, item) => {
    if (item.type === "module") return sum;
    if (item.type === "mock") {
      return sum + (item.mock.durationSeconds || 0);
    }
    if (item.visualStatus === "completed") return sum;
    const dur = item.lesson.durationSeconds || 0;
    const progress = item.lesson.progress || 0;
    return sum + dur * (1 - progress);
  }, 0);
}

// ── component ───────────────────────────────────────────────────────────────

export default function StudyPlanContinueCard({
  day,
  items,
  onPlayLesson,
}) {
  const isToday = !!day.isToday;
  const [expanded, setExpanded] = useState(isToday);
  const pill = getStatusPill(day);
  const remainingSec = getRemainingSeconds(items);
  const budgetSec = day.dailyBudgetSeconds || 0;
  const isOverBudget = remainingSec > budgetSec + 60;
  const progress = Math.min(day.dailyProgressPercent || 0, 100);
  const todayCompleted = isToday && progress >= 100;

  const headerClickProps = !isToday
    ? {
        onClick: () => setExpanded((prev) => !prev),
        style: { cursor: "pointer" },
      }
    : {};

  return (
    <div className={cardClass}>
      <div className={headerClass} {...headerClickProps}>
        <div className={headerLeftClass}>
          <div className={headerTitleAreaClass}>
            <h3 style={titleStyle}>
              {isToday
                ? "Hoje"
                : `${day.displayDate} - ${getWeekdayName(day.date)}`}
            </h3>
            <div className={progressBarWrapperClass}>
              <div className={progressBarTrackClass}>
                <div
                  className={todayCompleted ? progressBarFillCompletedClass : progressBarFillClass}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span style={progressLabelStyle}>
                {Math.round(progress)}% da meta diária
              </span>
            </div>
          </div>
        </div>

        <div className={headerRightClass}>
          {pill && <span className={pill.className}>{pill.label}</span>}
          {remainingSec > 0 && (
            <span style={remainingTimeStyle}>
              Tempo estimado restante{" "}
              <strong style={isOverBudget ? remainingTimeBoldAlertStyle : remainingTimeBoldStyle}>
                {formatRemainingTime(remainingSec)}
              </strong>
            </span>
          )}
          {!isToday && (
            <span className={collapseToggleClass}>
              {expanded ? "Ocultar" : "Ver aulas"}
              <i
                className="chevron down icon"
                style={chevronStyle(expanded)}
              />
            </span>
          )}
        </div>
      </div>

      {expanded && (
        <div className={lessonListClass}>
          {items.map((item, idx) =>
            item.type === "module" ? (
              <div key={`module-${idx}`} className={moduleHeaderClass}>
                {item.name}
              </div>
            ) : item.type === "mock" ? (
              <StudyPlanMockExamRow
                key={`mock-${item.mock.moduleNumber}-${idx}`}
                mock={item.mock}
                overBudget={item.overBudget}
              />
            ) : (
              <StudyPlanLessonRow
                key={item.lesson.id || idx}
                lesson={item.lesson}
                visualStatus={item.visualStatus}
                onPlay={onPlayLesson}
                isToday={isToday}
                overBudget={item.overBudget}
              />
            )
          )}
        </div>
      )}
    </div>
  );
}
